// ui/src/components/rep/RepCreditList.jsx
/**
 * @file RepCreditList.jsx
 * @module ui/src/components/rep/RepCreditList
 * @description Rep credits table — built for volume (200-500 credits).
 *
 * S805 REBUILD:
 *   SCROLLABLE  : fixed-height container (max 520px), overflow-y scroll, sticky header
 *   SEARCHABLE  : debounced 300ms on deal name, deal ID, event date
 *   SORTABLE    : Event Date (asc/desc), Credited Amount (asc/desc) — client-side
 *   ONE ROW     : deal ID (if present) + deal name + deal amount + deal date — holistic view
 *   CLICKABLE   : expand row for split/routing detail when present
 *   FILTERABLE  : All / Approved / Pending / Exception status pills
 *
 * @locked-rules
 *   C-39: deal_close_date is current source event date fallback until m_tc_04b.
 *   XC-6: Zero is valid currency; null/undefined = unavailable.
 *   XC-7: Credit status explicitly named; color reinforces.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const FILTERS = [
  { value: 'all',       label: 'All' },
  { value: 'approved',  label: 'Approved' },
  { value: 'pending',   label: 'Pending' },
  { value: 'exception', label: 'Exception' },
];

function formatCurrency(value, currencyCode) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode || 'USD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—';
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}%`;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
}

function statusLabel(status) {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
}

// Sort icon — ▲ ▼ or neutral
function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <span className="rcl__sort-icon rcl__sort-icon--idle" aria-hidden="true">↕</span>;
  return <span className="rcl__sort-icon rcl__sort-icon--active" aria-hidden="true">{sortDir === 'asc' ? '▲' : '▼'}</span>;
}

function RepCreditList({ positionId, fiscalPeriodId }) {
  const [data,        setData]        = useState(null);
  const [filter,      setFilter]      = useState('all');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [retryKey,    setRetryKey]    = useState(0);
  const [query,       setQuery]       = useState('');
  const [debouncedQ,  setDebouncedQ]  = useState('');
  const [sortCol,     setSortCol]     = useState('date');   // 'date' | 'amount'
  const [sortDir,     setSortDir]     = useState('desc');   // 'asc' | 'desc'
  const debounceRef = useRef(null);

  // Debounce search 300ms
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Fetch
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetch(
      `/api/v1/rep/${encodeURIComponent(positionId)}/credits?period=${encodeURIComponent(fiscalPeriodId)}`,
      { signal: controller.signal }
    )
      .then((r) => { if (!r.ok) throw new Error(`Unable to load credits (${r.status}).`); return r.json(); })
      .then(setData)
      .catch((e) => { if (e.name !== 'AbortError') setError(e); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });

    return () => controller.abort();
  }, [positionId, fiscalPeriodId, retryKey]);

  const toggleExpanded = useCallback((creditId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(creditId) ? next.delete(creditId) : next.add(creditId);
      return next;
    });
  }, []);

  const handleSort = useCallback((col) => {
    setSortCol((prev) => {
      if (prev === col) { setSortDir((d) => d === 'asc' ? 'desc' : 'asc'); return col; }
      setSortDir('desc');
      return col;
    });
  }, []);

  // Filter → search → sort
  const displayed = useMemo(() => {
    let credits = data?.credits ?? [];

    // Status filter
    if (filter !== 'all') credits = credits.filter((c) => c.creditStatus === filter);

    // Debounced search — deal name, deal ID, event date
    if (debouncedQ) {
      const q = debouncedQ.toLowerCase();
      credits = credits.filter((c) =>
        (c.dealName ?? '').toLowerCase().includes(q) ||
        (c.dealId   ?? '').toLowerCase().includes(q) ||
        (c.sourceEventDate ?? '').toLowerCase().includes(q)
      );
    }

    // Sort
    credits = [...credits].sort((a, b) => {
      let diff = 0;
      if (sortCol === 'date') {
        const da = a.sourceEventDate ? new Date(a.sourceEventDate).getTime() : 0;
        const db = b.sourceEventDate ? new Date(b.sourceEventDate).getTime() : 0;
        diff = da - db;
      } else if (sortCol === 'amount') {
        diff = (Number(a.creditedAmount) || 0) - (Number(b.creditedAmount) || 0);
      }
      return sortDir === 'asc' ? diff : -diff;
    });

    return credits;
  }, [data, filter, debouncedQ, sortCol, sortDir]);

  const hasExpandableDetail = (credit) =>
    credit.hasLayer0Split === true || credit.territoryRouting?.applies === true;

  if (loading) {
    return (
      <section className="rcl" aria-label="Credits" aria-busy="true">
        <div className="skeleton rcl__toolbar-skeleton" />
        <div className="skeleton rcl__table-skeleton" />
        <style>{styles}</style>
      </section>
    );
  }

  if (error) {
    return (
      <section className="rcl__error" role="alert">
        <h3>Unable to load credits</h3>
        <p>{error.message}</p>
        <button type="button" className="secondary-button" onClick={() => setRetryKey((v) => v + 1)}>
          Retry
        </button>
        <style>{styles}</style>
      </section>
    );
  }

  return (
    <section className="rcl" aria-labelledby="rcl-title">
      {/* ── Toolbar: title + search + filters ── */}
      <header className="rcl__header">
        <div>
          <p className="rcl__eyebrow">Performance Credit</p>
          <h2 id="rcl-title" className="rcl__title">Credits</h2>
        </div>

        <div className="rcl__toolbar">
          <input
            type="search"
            className="rcl__search"
            placeholder="Search deal, ID, date…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search credits"
          />

          <div className="rcl__filters" aria-label="Filter by status">
            {FILTERS.map((item) => (
              <button
                key={item.value}
                type="button"
                className="rcl__filter"
                aria-pressed={filter === item.value}
                data-state={filter === item.value ? 'selected' : 'idle'}
                onClick={() => setFilter(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <p className="rcl__date-note">
        Event Date reflects the source economic event date used for this credit.
      </p>

      {/* ── Scrollable table ── */}
      {displayed.length === 0 ? (
        <div className="rcl__empty">
          {debouncedQ
            ? `No credits match "${debouncedQ}".`
            : `No ${filter !== 'all' ? filter : ''} credits found for this period.`}
        </div>
      ) : (
        <div className="rcl__table-wrap" role="region" aria-label="Credits table">
          <table className="rcl__table" role="grid" aria-label="Period credits">
            <thead className="rcl__thead">
              <tr>
                <th scope="col" className="rcl__th">Deal / Event</th>
                <th scope="col" className="rcl__th rcl__th--id">Deal ID</th>
                <th
                  scope="col"
                  className="rcl__th rcl__th--num rcl__th--sortable"
                  onClick={() => handleSort('date')}
                  aria-sort={sortCol === 'date' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  Event Date <SortIcon col="date" sortCol={sortCol} sortDir={sortDir} />
                </th>
                <th
                  scope="col"
                  className="rcl__th rcl__th--num rcl__th--sortable"
                  onClick={() => handleSort('amount')}
                  aria-sort={sortCol === 'amount' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  Deal Amount <SortIcon col="amount" sortCol={sortCol} sortDir={sortDir} />
                </th>
                <th scope="col" className="rcl__th rcl__th--num">Credit %</th>
                <th scope="col" className="rcl__th rcl__th--num">Credited Value</th>
                <th scope="col" className="rcl__th">Status</th>
                <th scope="col" className="rcl__th rcl__th--action">
                  <span className="rcl__sr-only">Detail</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {displayed.map((credit) => {
                const expanded    = expandedIds.has(credit.creditId);
                const detailRowId = `rcl-detail-${credit.creditId}`;
                const canExpand   = hasExpandableDetail(credit);

                return (
                  <Fragment key={credit.creditId}>
                    <tr
                      className={`rcl__row${expanded ? ' rcl__row--expanded' : ''}`}
                      data-state={credit.creditStatus}
                    >
                      {/* Deal name — primary identifier */}
                      <td className="rcl__td">
                        <strong className="rcl__deal-name">{credit.dealName || '—'}</strong>
                      </td>

                      {/* Deal ID — show when present */}
                      <td className="rcl__td rcl__td--id">
                        {credit.dealId ? (
                          <span className="rcl__deal-id">{credit.dealId}</span>
                        ) : (
                          <span className="rcl__no-id">—</span>
                        )}
                      </td>

                      {/* Event date */}
                      <td className="rcl__td rcl__td--num">
                        {formatDate(credit.sourceEventDate)}
                      </td>

                      {/* Deal amount (creditedAmount = raw deal amount before credit %) */}
                      <td className="rcl__td rcl__td--num">
                        {formatCurrency(credit.creditedAmount, credit.creditedAmountCurrency)}
                      </td>

                      {/* Credit % */}
                      <td className="rcl__td rcl__td--num">
                        {formatPercent(credit.creditPct)}
                      </td>

                      {/* Credited value (after credit %) */}
                      <td className="rcl__td rcl__td--num rcl__td--value">
                        {formatCurrency(credit.resultingCreditedValue, credit.resultingCreditedValueCurrency)}
                      </td>

                      {/* Status pill */}
                      <td className="rcl__td">
                        <span className={`rcl__status rcl__status--${credit.creditStatus}`} data-state={credit.creditStatus}>
                          {statusLabel(credit.creditStatus)}
                        </span>
                      </td>

                      {/* Expand toggle — only when split/routing detail exists */}
                      <td className="rcl__td rcl__td--action">
                        {canExpand ? (
                          <button
                            type="button"
                            className="icon-button rcl__expand-btn"
                            aria-expanded={expanded}
                            aria-controls={detailRowId}
                            aria-label={`${expanded ? 'Collapse' : 'Expand'} detail for ${credit.dealName}`}
                            onClick={() => toggleExpanded(credit.creditId)}
                          >
                            {expanded ? '−' : '+'}
                          </button>
                        ) : null}
                      </td>
                    </tr>

                    {expanded && canExpand ? (
                      <tr id={detailRowId} className="rcl__detail-row">
                        <td colSpan={8}>
                          <div className="rcl__detail-panel">
                            {credit.hasLayer0Split === true && credit.layer0SplitExplanation?.label ? (
                              <p>{credit.layer0SplitExplanation.label}</p>
                            ) : null}
                            {credit.territoryRouting?.applies === true && credit.territoryRouting.explanation ? (
                              <p>{credit.territoryRouting.explanation}</p>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>

            <tfoot className="rcl__tfoot">
              <tr>
                <td colSpan={5}>
                  <strong>{data?.summary?.totalCredits ?? 0} total credits</strong>
                  {debouncedQ || filter !== 'all' ? (
                    <span className="rcl__filter-note"> · {displayed.length} shown</span>
                  ) : null}
                </td>
                <td className="rcl__td--num rcl__tfoot-total">
                  <span className="rcl__footer-label">Total credited value </span>
                  <strong>{formatCurrency(data?.summary?.totalCreditedValue, data?.summary?.currencyCode)}</strong>
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <style>{styles}</style>
    </section>
  );
}

RepCreditList.propTypes = {
  positionId:     PropTypes.string.isRequired,
  fiscalPeriodId: PropTypes.number.isRequired,
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = `
  .rcl {
    overflow: hidden;
    border: 1px solid rgb(var(--color-border));
    border-radius: 12px;
    background: rgb(var(--color-surface));
    color: rgb(var(--color-text));
  }

  /* ── Header ── */
  .rcl__header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid rgb(var(--color-border));
  }

  .rcl__eyebrow {
    margin: 0 0 2px;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .rcl__title { margin: 0; font-size: 15px; font-weight: 600; }

  /* ── Toolbar: search + filters ── */
  .rcl__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }

  .rcl__search {
    height: 32px;
    padding: 0 10px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 8px;
    background: rgb(var(--color-surface));
    color: rgb(var(--color-text));
    font-size: 12px;
    outline: none;
    width: 200px;
  }

  .rcl__search:focus {
    border-color: rgb(var(--color-primary) / 0.5);
    box-shadow: 0 0 0 2px rgb(var(--color-primary) / 0.12);
  }

  .rcl__filters { display: flex; flex-wrap: wrap; gap: 6px; }

  .rcl__filter {
    padding: 3px 10px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    background: rgb(var(--color-surface));
    color: rgb(var(--color-text-muted));
    font-size: 11px;
    cursor: pointer;
  }

  .rcl__filter[data-state="selected"] {
    border-color: rgb(var(--color-primary) / 0.4);
    background: rgb(var(--color-primary) / 0.06);
    color: rgb(var(--color-primary));
    font-weight: 600;
  }

  .rcl__filter:focus-visible { outline: 2px solid rgb(var(--color-focus-ring)); outline-offset: 2px; }

  /* ── Date note ── */
  .rcl__date-note {
    margin: 0;
    padding: 6px 16px;
    border-bottom: 1px solid rgb(var(--color-border));
    color: rgb(var(--color-text-muted));
    font-size: 11px;
  }

  /* ── Scrollable table wrap — fixed height for volume (S805) ── */
  .rcl__table-wrap {
    overflow-y: auto;
    max-height: 520px;
  }

  /* ── Table ── */
  .rcl__table {
    width: 100%;
    min-width: 780px;
    border-collapse: collapse;
    font-size: 12px;
  }

  .rcl__thead {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .rcl__th {
    padding: 9px 12px;
    border-bottom: 1px solid rgb(var(--color-border));
    background: rgb(var(--color-surface-muted));
    text-align: left;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
    user-select: none;
  }

  .rcl__th--num { text-align: right; }
  .rcl__th--id  { color: rgb(var(--color-text-muted) / 0.7); }
  .rcl__th--action { width: 44px; }

  .rcl__th--sortable {
    cursor: pointer;
  }

  .rcl__th--sortable:hover { color: rgb(var(--color-text)); }

  .rcl__sort-icon { margin-left: 4px; font-size: 9px; }
  .rcl__sort-icon--idle { opacity: 0.35; }
  .rcl__sort-icon--active { color: rgb(var(--color-primary)); opacity: 1; }

  /* ── Rows ── */
  .rcl__row { border-bottom: 1px solid rgb(var(--color-border) / 0.6); transition: background 0.1s; }
  .rcl__row:hover { background: rgb(var(--color-surface-muted)); }
  .rcl__row:last-child { border-bottom: 0; }
  .rcl__row--expanded { background: rgb(var(--color-primary) / 0.03); }

  /* ── Cells ── */
  .rcl__td {
    padding: 10px 12px;
    vertical-align: middle;
  }

  .rcl__td--num   { text-align: right; font-variant-numeric: tabular-nums; }
  .rcl__td--id    { color: rgb(var(--color-text-muted)); }
  .rcl__td--value { font-weight: 600; }
  .rcl__td--action { text-align: center; }

  .rcl__deal-name { font-weight: 600; font-size: 12px; }

  .rcl__deal-id {
    display: inline-block;
    padding: 1px 6px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 4px;
    background: rgb(var(--color-surface-muted));
    font-size: 10px;
    font-family: monospace;
    color: rgb(var(--color-text-muted));
  }

  .rcl__no-id { color: rgb(var(--color-border)); }

  /* ── Status pills ── */
  .rcl__status {
    display: inline-flex;
    padding: 2px 9px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
  }

  .rcl__status--approved { border-color: rgb(var(--color-success)/0.25); background: rgb(var(--color-success)/0.05); color: rgb(var(--color-success)); }
  .rcl__status--pending  { border-color: rgb(var(--color-warning)/0.3);  background: rgb(var(--color-warning)/0.07); color: rgb(var(--color-warning)); }
  .rcl__status--exception { border-color: rgb(var(--color-danger)/0.25);  background: rgb(var(--color-danger)/0.05);  color: rgb(var(--color-danger)); }

  /* ── Expand button ── */
  .rcl__expand-btn { font-size: 14px; }
  .rcl__expand-btn:focus-visible { outline: 2px solid rgb(var(--color-focus-ring)); outline-offset: 2px; }

  /* ── Detail row ── */
  .rcl__detail-row td { padding: 0; background: rgb(var(--color-surface-muted)); }

  .rcl__detail-panel {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 16px 12px;
    border-left: 3px solid rgb(var(--color-primary) / 0.35);
  }

  .rcl__detail-panel p { margin: 0; color: rgb(var(--color-text-muted)); font-size: 12px; line-height: 1.45; }

  /* ── Footer ── */
  .rcl__tfoot td {
    padding: 10px 12px;
    border-top: 1px solid rgb(var(--color-border));
    background: rgb(var(--color-surface-muted));
    font-size: 12px;
    vertical-align: middle;
  }

  .rcl__tfoot-total { text-align: right; }
  .rcl__footer-label { color: rgb(var(--color-text-muted)); }
  .rcl__filter-note { color: rgb(var(--color-text-muted)); font-weight: 400; }

  /* ── Empty / error ── */
  .rcl__empty {
    margin: 16px;
    padding: 24px 16px;
    border: 1px dashed rgb(var(--color-border));
    border-radius: 8px;
    background: rgb(var(--color-surface-muted));
    color: rgb(var(--color-text-muted));
    font-size: 12px;
    text-align: center;
  }

  .rcl__error {
    padding: 16px 12px;
    border: 1px solid rgb(var(--color-danger) / 0.2);
    border-radius: 8px;
    background: rgb(var(--color-danger) / 0.05);
  }

  .rcl__error h3 { margin: 0 0 5px; font-size: 13px; }
  .rcl__error p  { margin: 0 0 12px; color: rgb(var(--color-text-muted)); font-size: 12px; }

  /* ── Loading skeletons ── */
  .rcl__toolbar-skeleton { height: 54px; }
  .rcl__table-skeleton   { height: 260px; margin: 12px; border-radius: 8px; }

  .rcl__sr-only {
    position: absolute; width: 1px; height: 1px;
    overflow: hidden; clip: rect(0,0,0,0);
    white-space: nowrap; clip-path: inset(50%);
  }
`;

export default RepCreditList;
